import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import T from 'utils/useLocalizer';
import { ISelectItem } from 'models/commonModel';
import { useFormContext } from 'react-hook-form';
import { IOrderSettingsFormData } from 'models/ordersModel';
import { ReceiptCutoff } from 'react-bootstrap-icons';
import FormLabel from 'components/form/label/FormLabel';
import { pageRoutes } from 'config/router/pageRoutes';
import SelectField from 'components/form/input/SelectField';
import { OrderWAPhoneEmptyContext } from './OrderWAPhoneEmptyContext';
import { IWhatsAppTemplate } from 'models/whatsAppTemplateModel';

export interface Props {
  templateList: IWhatsAppTemplate[];
  isLoadingTemplates: boolean;
}

const InvoiceTemplateForm = ({ templateList, isLoadingTemplates }: Props) => {
  const navigate = useNavigate();
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<IOrderSettingsFormData>();

  const isDisabled = useContext(OrderWAPhoneEmptyContext);
  const [waInvoiceTemplateList, setWaInvoiceTemplateList] = useState<ISelectItem<number>[]>([]);
  const invoiceTemplateWatch = watch('invoiceWATemplateId');

  const resources = {
    invoiceTemplateLabel: T('orders.order.settings.invoice.template.label'),
    invoiceTemplateLabelDesc: T('orders.order.settings.invoice.template.label.description'),
    invoiceTemplateHint: T('orders.order.settings.invoice.template.hint'),
    invoiceTemplatePlaceholder: T('orders.order.settings.invoice.template.placeholder'),
    addInvoiceTemplateButtonLabel: T('orders.order.settings.invoice.template.add.invoice.template.button.label'),
    missingInvoiceTemplateMessage: T('orders.order.settings.invoice.template.missing.invoice.template.message'),
  };

  useEffect(() => {
    if (templateList && templateList.length > 0) {
      const newTemplateList = templateList.map((item) => ({ label: `${item.name}`, value: item.id }));
      setWaInvoiceTemplateList(newTemplateList);
    }
  }, [templateList]);

  useEffect(() => {
    if (waInvoiceTemplateList.length > 0 && invoiceTemplateWatch !== undefined) {
      const exists = waInvoiceTemplateList.some((t) => t.value === invoiceTemplateWatch);
      setValue('invoiceWATemplateId', exists ? invoiceTemplateWatch : -1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waInvoiceTemplateList]);

  return (
    <div className="border rounded-12 bg-white p-3">
      <div className="d-flex gap-2 align-items-center mb-4">
        <ReceiptCutoff className="text-primary me-2" />
        <FormLabel
          label={resources.invoiceTemplateLabel}
          labelDesc={resources.invoiceTemplateLabelDesc}
          hint={resources.invoiceTemplateHint}
          hintId="invoice-template-label-hint"
          isLabelInline
        />
      </div>

      <div>
        {waInvoiceTemplateList.length === 0 && !isLoadingTemplates && (
          <div className="px-2 mb-4">
            <span className="text-muted">
              {resources.missingInvoiceTemplateMessage}{' '}
              <span className="text-primary cursor-pointer text-decoration-underline" onClick={() => navigate(pageRoutes.whatsAppTemplates)}>
                {resources.addInvoiceTemplateButtonLabel}
              </span>
            </span>
          </div>
        )}
      </div>
      <SelectField
        inputProps={{ ...register('invoiceWATemplateId') }}
        disabled={waInvoiceTemplateList.length === 0 || isDisabled}
        itemsList={waInvoiceTemplateList}
        customClassName="col-lg-4 mb-2"
        defaultOptionLabel={resources.invoiceTemplatePlaceholder}
        isLoading={isLoadingTemplates}
        isError={!!errors.invoiceWATemplateId}
        errorMessage={errors.invoiceWATemplateId?.message}
      />
    </div>
  );
};

export default InvoiceTemplateForm;
